/**
 * Shared manufacturer-warranty period authority.
 * Missing research must never silently become 12 months.
 */

import { normalizeWarrantyBrandKey } from "./warrantyProductIdentity";

export const DEFAULT_UNVERIFIED_WARRANTY_MONTHS = 12;

/** Legacy source still readable on historical rows. Never apply as a new default. */
export const DEFAULT_UNVERIFIED_WARRANTY_SOURCE =
  "default_12_months_unverified" as const;

export const DEFAULT_UNVERIFIED_WARRANTY_ACTION_MESSAGE =
  "Warranty period could not be verified. A 12-month default was applied.";

export const MANUFACTURER_WARRANTY_UNVERIFIED_SOURCE =
  "manufacturer_unverified" as const;

export const MANUFACTURER_WARRANTY_UNVERIFIED_LABEL =
  "Manufacturer warranty not yet verified";

export const MANUFACTURER_WARRANTY_UNVERIFIED_MESSAGE =
  "Manufacturer warranty is not yet verified. Staff must verify from an official manufacturer source or enter the warranty before finalising.";

export const STAFF_CONFIRMED_WARRANTY_SOURCE = "staff_confirmed" as const;

export const WARRANTY_PERIOD_SOURCES = [
  "catalogue_explicit",
  "proposal_item_explicit",
  "configured_product_explicit",
  "manufacturer_research",
  DEFAULT_UNVERIFIED_WARRANTY_SOURCE,
  MANUFACTURER_WARRANTY_UNVERIFIED_SOURCE,
  "manual_entry_default",
  STAFF_CONFIRMED_WARRANTY_SOURCE,
  "admin_override",
] as const;
export type WarrantyPeriodSource = (typeof WARRANTY_PERIOD_SOURCES)[number];

export const WARRANTY_VERIFICATION_STATES = [
  "verified",
  "staff_confirmed",
  "pending",
  "unknown",
] as const;
export type WarrantyVerificationState = (typeof WARRANTY_VERIFICATION_STATES)[number];

export type WarrantyPeriodEvidence = {
  sourceType: WarrantyPeriodSource;
  reason?: string;
  sourceUrl?: string | null;
  sourceTitle?: string | null;
  retrievedAt?: string | null;
  matchedBrand?: string | null;
  matchedModel?: string | null;
  identityKey?: string | null;
  confidence?: "high" | "medium" | "low" | null;
  market?: string | null;
  quote?: string | null;
  notes?: string | null;
  registrationRequired?: boolean | null;
  verificationStatus?: WarrantyVerificationState | null;
  verifierType?: "openai_web" | "staff" | "registry" | "system" | null;
  modelToolVersion?: string | null;
  confirmedByUserId?: number | null;
  confirmedByName?: string | null;
  confirmedAt?: string | null;
};

export function isStaffConfirmedWarrantySource(
  source: string | null | undefined,
): boolean {
  return (
    source === STAFF_CONFIRMED_WARRANTY_SOURCE ||
    source === "admin_override" ||
    source === "manual_entry_default"
  );
}

export function isVerifiedManufacturerWarrantySource(
  source: string | null | undefined,
): boolean {
  return source === "manufacturer_research";
}

export function isUnverifiedWarrantySource(source: string | null | undefined): boolean {
  return (
    source === DEFAULT_UNVERIFIED_WARRANTY_SOURCE ||
    source === MANUFACTURER_WARRANTY_UNVERIFIED_SOURCE ||
    source == null ||
    source === ""
  );
}

export function warrantyVerificationStateFromSource(
  source: string | null | undefined,
  months: number | null | undefined,
): WarrantyVerificationState {
  if (isStaffConfirmedWarrantySource(source) && months && months > 0) return "staff_confirmed";
  if (isVerifiedManufacturerWarrantySource(source) && months && months > 0) return "verified";
  if (isUnverifiedWarrantySource(source) || !months) return months ? "unknown" : "pending";
  return "unknown";
}

export function canPresentWarrantyAsVerified(source: string | null | undefined, months: number | null | undefined): boolean {
  const state = warrantyVerificationStateFromSource(source, months);
  return state === "verified" || state === "staff_confirmed";
}

const LEGACY_UNVERIFIED_WARRANTY_LABEL_RE =
  /(?:12\s*months?\s*default|pending\s+verification|not\s+yet\s+verified)/i;

export function isLegacyUnverifiedWarrantyLabel(
  label: string | null | undefined,
): boolean {
  return LEGACY_UNVERIFIED_WARRANTY_LABEL_RE.test(String(label ?? "").trim());
}

export function formatManufacturerWarrantyPeriodLabel(months: number): string {
  if (months === 12) return "1-year manufacturer warranty";
  if (months > 0 && months % 12 === 0) {
    return `${months / 12}-year manufacturer warranty`;
  }
  if (months === 1) return "1-month manufacturer warranty";
  return `${months}-month manufacturer warranty`;
}

/**
 * Presentation authority for the human-readable warranty label. A stale legacy
 * fallback label must never contradict the numeric duration/source that drives
 * the expiry date.
 */
export function resolveManufacturerWarrantyDisplayLabel(input: {
  storedLabel?: string | null;
  months?: number | null;
  source?: string | null;
}): string {
  const stored = String(input.storedLabel ?? "").trim();
  const months = Number(input.months ?? 0);
  if (!input.source) {
    if (stored && !isLegacyUnverifiedWarrantyLabel(stored)) return stored;
    if (months > 0 && months !== 12) {
      return formatManufacturerWarrantyPeriodLabel(months);
    }
    return MANUFACTURER_WARRANTY_UNVERIFIED_LABEL;
  }
  if (!canPresentWarrantyAsVerified(input.source, months)) {
    return MANUFACTURER_WARRANTY_UNVERIFIED_LABEL;
  }
  if (!stored || isLegacyUnverifiedWarrantyLabel(stored)) {
    return formatManufacturerWarrantyPeriodLabel(months);
  }
  return stored;
}

export const T_PLUS_A_OFFICIAL_HOSTS = [
  "ta-hifi.de",
  "www.ta-hifi.de",
  "ta-hifi.com",
  "www.ta-hifi.com",
] as const;

export const MANUFACTURER_OFFICIAL_HOSTS: Record<string, readonly string[]> = {
  "t+a": T_PLUS_A_OFFICIAL_HOSTS,
};

const NON_AUTHORITATIVE_HOST_RE =
  /(amazon|ebay|carousell|shopee|lazada|qoo10|bestbuy|reddit|forum|youtube|marketplace|facebook|instagram)/i;

export function looksLikeAuthoritativeManufacturerHost(
  brandKey: string,
  hostname: string,
): boolean {
  const host = hostname.toLowerCase().replace(/^www\./, "");
  if (!host || NON_AUTHORITATIVE_HOST_RE.test(host)) return false;
  const mapped = MANUFACTURER_OFFICIAL_HOSTS[brandKey] ?? [];
  if (mapped.some((allowed) => allowed.replace(/^www\./, "") === host || host.endsWith(`.${allowed.replace(/^www\./, "")}`))) {
    return true;
  }
  const brandTokens = brandKey
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 3);
  if (brandTokens.length === 0) return false;
  return brandTokens.some((token) => host.includes(token));
}

export function looksLikeAuthoritativeManufacturerUrl(
  brand: string,
  url: string | null | undefined,
): boolean {
  const value = String(url ?? "").trim();
  if (!value) return false;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  return looksLikeAuthoritativeManufacturerHost(normalizeWarrantyBrandKey(brand), parsed.hostname);
}

export type CuratedManufacturerWarranty = {
  identityKey: string;
  brand: string;
  model: string;
  months: number;
  label: string;
  registrationRequired: boolean;
  sourceUrl: string;
  sourceTitle: string;
  quote: string;
};

export const CURATED_MANUFACTURER_WARRANTIES: CuratedManufacturerWarranty[] = [
  {
    identityKey: "t+a|p|3100|hv",
    brand: "T+A",
    model: "P 3100 HV",
    months: 36,
    label: "3-year manufacturer warranty (registration required)",
    registrationRequired: true,
    sourceUrl: "https://www.ta-hifi.de/wp-content/uploads/Audio_8_2020_P_3100_HV.pdf",
    sourceTitle: "Audio 8/2020 — T+A P 3100 HV (manufacturer-hosted publication)",
    quote: "Garantiezeit 3 Jahre (nach Reg.)",
  },
];

export function curatedManufacturerWarranty(
  identityKey: string,
): CuratedManufacturerWarranty | null {
  return CURATED_MANUFACTURER_WARRANTIES.find((row) => row.identityKey === identityKey) ?? null;
}
